import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import {
  AUTOCOMPLETE_DELAY_MS,
  MIN_AUTOCOMPLETE_CHARS,
  searchMockAthenaConcepts,
} from '../__mocks__/mockAthenaConcepts';

const CriteriaParameterAutocomplete = ({ label = 'Field', value = '', onChange }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const query = String(value || '');
  const hasEnoughChars = query.trim().length >= MIN_AUTOCOMPLETE_CHARS;

  useEffect(() => {
    let active = true;
    let timer = null;

    if (!hasEnoughChars) {
      setSuggestions([]);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    timer = window.setTimeout(() => {
      if (!active) return;
      setSuggestions(searchMockAthenaConcepts(query));
      setLoading(false);
    }, AUTOCOMPLETE_DELAY_MS);

    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [hasEnoughChars, query]);

  const noOptionsText = useMemo(() => {
    if (!hasEnoughChars) {
      return `Type at least ${MIN_AUTOCOMPLETE_CHARS} characters`;
    }
    return 'No matching concepts';
  }, [hasEnoughChars]);

  return (
    <Autocomplete
      freeSolo
      fullWidth
      autoHighlight
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      open={open && (loading || hasEnoughChars)}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      inputValue={query}
      options={suggestions}
      loading={loading}
      noOptionsText={noOptionsText}
      filterOptions={(options) => options}
      groupBy={(option) => option.category || 'Other'}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option?.name || '';
      }}
      onInputChange={(_, nextInputValue, reason) => {
        if (reason === 'reset') return;
        setOpen(nextInputValue.trim().length >= MIN_AUTOCOMPLETE_CHARS);
        onChange?.(nextInputValue);
      }}
      onChange={(_, selectedValue) => {
        if (typeof selectedValue === 'string') {
          setOpen(false);
          onChange?.(selectedValue);
          return;
        }
        if (selectedValue && typeof selectedValue === 'object') {
          setOpen(false);
          onChange?.(selectedValue.name);
          return;
        }
        setOpen(false);
        onChange?.('');
      }}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            minWidth: 0,
          }}
        >
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {option.name}
            </Typography>
          </Box>
          {option.llmSuggested ? (
            <Chip
              size="small"
              variant="outlined"
              color="secondary"
              icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
              label="LLM-suggested"
            />
          ) : null}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default CriteriaParameterAutocomplete;
